import { useEffect, useState, useCallback, useRef } from 'react';
import { apiClient } from '@/services/api';
import { useIsBootstrapMode } from '@connor-music/shared';
import { adminApiClient } from '@/services/adminApi';
import { FormField, ConfirmDialog, Skeleton } from '@connor-music/shared';
import { EntraSsoSetupGuide } from '@/components/EntraSsoSetupGuide';
import {
  Fingerprint,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Power,
  PowerOff,
  Eye,
  EyeOff,
  Pencil,
} from 'lucide-react';

interface ProviderConfig {
  tenant_id?: string;
  client_id?: string;
  client_secret?: string;
  saml_metadata_url?: string;
  domain?: string;
  service_account_json?: string;
  admin_email?: string;
  [key: string]: unknown;
}

interface SsoStatus {
  active_provider: string | null;
  cognito_entity_id: string;
  cognito_acs_url: string;
  providers: Record<string, { configured: boolean; enabled: boolean; config?: ProviderConfig; sign_on_url?: string }>;
}

interface IdpGroup {
  id: string;
  name: string;
  email?: string;
  description: string | null;
}

type ActiveTab = 'microsoft-entra' | 'google-workspace';

export default function AdminIdentity() {
  const isBootstrap = useIsBootstrapMode();
  const api = isBootstrap ? adminApiClient : apiClient;

  const [ssoStatus, setSsoStatus] = useState<SsoStatus | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('microsoft-entra');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Microsoft Entra form state
  const [entraForm, setEntraForm] = useState({
    tenantId: '',
    clientId: '',
    clientSecret: '',
    samlMetadataUrl: '',
  });
  const [entraHasStoredSecret, setEntraHasStoredSecret] = useState(false);
  const [entraSubmitting, setEntraSubmitting] = useState(false);

  // Google Workspace form state
  const [googleHasStoredServiceAccount, setGoogleHasStoredServiceAccount] = useState(false);
  const [googleForm, setGoogleForm] = useState({
    domain: '',
    samlMetadataUrl: '',
    serviceAccountJson: '',
    adminEmail: '',
  });
  const [googleSubmitting, setGoogleSubmitting] = useState(false);

  // Groups
  const [groups, setGroups] = useState<IdpGroup[]>([]);
  const [fetchingGroups, setFetchingGroups] = useState(false);

  // Role mappings
  const [adminGroupIds, setAdminGroupIds] = useState<string[]>([]);
  const [employeeGroupIds, setEmployeeGroupIds] = useState<string[]>([]);
  const [leadershipGroupIds, setLeadershipGroupIds] = useState<string[]>([]);
  const [savingMappings, setSavingMappings] = useState(false);

  // Activate/deactivate
  const [activating, setActivating] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);

  // Field visibility (obfuscation)
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());
  const toggleFieldVisibility = (fieldId: string) => {
    setVisibleFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  };

  // Group mapping edit mode
  const [editingMappings, setEditingMappings] = useState(false);

  const messagesRef = useRef<HTMLDivElement>(null);

  const loadSsoStatus = useCallback(async () => {
    try {
      const status = await api.getSsoStatus();
      setSsoStatus(status);

      // Populate form fields from saved config
      const entraConfig = status.providers['microsoft-entra']?.config;
      if (entraConfig) {
        // Track whether a secret is stored (API returns "********" for stored secrets)
        if (entraConfig.client_secret === '********') {
          setEntraHasStoredSecret(true);
        }
        setEntraForm((prev) => ({
          tenantId: entraConfig.tenant_id || prev.tenantId,
          clientId: entraConfig.client_id || prev.clientId,
          // Don't populate with masked value — keep empty unless user types a new one
          clientSecret: prev.clientSecret || '',
          samlMetadataUrl: entraConfig.saml_metadata_url || prev.samlMetadataUrl,
        }));
      }

      const googleConfig = status.providers['google-workspace']?.config;
      if (googleConfig) {
        if (googleConfig.service_account_json === '********') {
          setGoogleHasStoredServiceAccount(true);
        }
        setGoogleForm((prev) => ({
          domain: googleConfig.domain || prev.domain,
          samlMetadataUrl: googleConfig.saml_metadata_url || prev.samlMetadataUrl,
          // Don't populate with masked value — keep empty unless user types a new one
          serviceAccountJson: prev.serviceAccountJson || '',
          adminEmail: googleConfig.admin_email || prev.adminEmail,
        }));
      }
    } catch (err) {
      console.error('Failed to load SSO status:', err);
      setError('Failed to load SSO status.');
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  const loadRoleMappings = useCallback(
    async (provider: string) => {
      try {
        const mappings = await api.getRoleMappings(provider);
        setAdminGroupIds(mappings.admin_group_ids);
        setEmployeeGroupIds(mappings.employee_group_ids);
        setLeadershipGroupIds(mappings.leadership_group_ids || []);
        if (mappings.cached_groups?.length) {
          setGroups(mappings.cached_groups);
        }
      } catch {
        // Role mappings may not exist yet
        setAdminGroupIds([]);
        setEmployeeGroupIds([]);
        setLeadershipGroupIds([]);
      }
    },
    [api]
  );

  useEffect(() => {
    loadSsoStatus();
  }, [loadSsoStatus]);

  useEffect(() => {
    if (ssoStatus?.providers[activeTab]?.configured) {
      loadRoleMappings(activeTab);
    }
  }, [activeTab, ssoStatus, loadRoleMappings]);

  // Scroll messages into view when they appear
  useEffect(() => {
    if ((error || successMessage) && messagesRef.current) {
      messagesRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error, successMessage]);

  const clearMessages = () => {
    setError('');
    setSuccessMessage('');
  };

  // -- Entra configuration --
  const handleConfigureEntra = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    setEntraSubmitting(true);

    try {
      const result = await api.configureMsEntra({
        tenantId: entraForm.tenantId,
        clientId: entraForm.clientId,
        clientSecret: entraForm.clientSecret || undefined,
        samlMetadataUrl: entraForm.samlMetadataUrl || undefined,
      });
      setSuccessMessage(result.message || 'Microsoft Entra ID configured successfully.');
      await loadSsoStatus();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to configure Microsoft Entra ID.';
      setError(message);
    } finally {
      setEntraSubmitting(false);
    }
  };

  // -- Google configuration --
  const handleConfigureGoogle = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    setGoogleSubmitting(true);

    try {
      const result = await api.configureGoogleWorkspace({
        domain: googleForm.domain,
        samlMetadataUrl: googleForm.samlMetadataUrl || undefined,
        serviceAccountJson: googleForm.serviceAccountJson || undefined,
        adminEmail: googleForm.adminEmail || undefined,
      });
      setSuccessMessage(result.message || 'Google Workspace configured successfully.');
      await loadSsoStatus();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to configure Google Workspace.';
      setError(message);
    } finally {
      setGoogleSubmitting(false);
    }
  };

  // -- Fetch groups --
  const handleFetchGroups = async () => {
    clearMessages();
    setFetchingGroups(true);
    setGroups([]);

    try {
      if (activeTab === 'microsoft-entra') {
        if (!entraForm.tenantId || !entraForm.clientId || (!entraForm.clientSecret && !entraHasStoredSecret)) {
          setError('Tenant ID, Client ID, and Client Secret are required to fetch groups.');
          setFetchingGroups(false);
          return;
        }
        const result = await api.fetchEntraGroups({
          tenantId: entraForm.tenantId,
          clientId: entraForm.clientId,
          clientSecret: entraForm.clientSecret,
        });
        setGroups(result.groups);
      } else {
        if ((!googleForm.serviceAccountJson && !googleHasStoredServiceAccount) || !googleForm.domain || !googleForm.adminEmail) {
          setError('Service Account JSON, Domain, and Admin Email are required to fetch groups.');
          setFetchingGroups(false);
          return;
        }
        const result = await api.fetchGoogleGroups({
          serviceAccountJson: googleForm.serviceAccountJson,
          domain: googleForm.domain,
          adminEmail: googleForm.adminEmail,
        });
        setGroups(result.groups);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch groups.';
      setError(message);
    } finally {
      setFetchingGroups(false);
    }
  };

  // -- Role mapping toggle --
  const toggleGroupRole = (groupId: string, role: 'admin' | 'leadership' | 'employee') => {
    if (role === 'admin') {
      setAdminGroupIds((prev) =>
        prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
      );
    } else if (role === 'leadership') {
      setLeadershipGroupIds((prev) =>
        prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
      );
    } else {
      setEmployeeGroupIds((prev) =>
        prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
      );
    }
  };

  // -- Save role mappings --
  const handleSaveRoleMappings = async () => {
    clearMessages();
    setSavingMappings(true);

    try {
      const result = await api.updateRoleMappings(activeTab, {
        admin_group_ids: adminGroupIds,
        employee_group_ids: employeeGroupIds,
        leadership_group_ids: leadershipGroupIds,
        cached_groups: groups.length > 0 ? groups : undefined,
      });
      setSuccessMessage(result.message || 'Role mappings saved successfully.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save role mappings.';
      setError(message);
    } finally {
      setSavingMappings(false);
    }
  };

  // -- Activate SSO --
  const handleActivateSso = async () => {
    clearMessages();
    setActivating(true);

    try {
      const result = await api.activateSso(activeTab);
      setSuccessMessage(result.message || 'SSO activated successfully.');
      await loadSsoStatus();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to activate SSO.';
      setError(message);
    } finally {
      setActivating(false);
    }
  };

  // -- Deactivate SSO --
  const handleDeactivateSso = async () => {
    clearMessages();
    setShowDeactivateDialog(false);
    setActivating(true);

    try {
      const result = await api.deactivateSso();
      setSuccessMessage(result.message || 'SSO deactivated successfully.');
      await loadSsoStatus();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to deactivate SSO.';
      setError(message);
    } finally {
      setActivating(false);
    }
  };

  const providerConfigured = ssoStatus?.providers[activeTab]?.configured ?? false;
  const providerActive = ssoStatus?.active_provider === activeTab;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-72" />
            <Skeleton className="h-4 w-96 max-w-full" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Skeleton className="h-20 rounded-lg" />
          <Skeleton className="h-20 rounded-lg" />
        </div>
        <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700 pb-3">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-5 w-36" />
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4">
          <Skeleton className="h-5 w-40" />
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-9 w-full rounded-md" />
            </div>
          ))}
          <div className="flex justify-end">
            <Skeleton className="h-9 w-36 rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  const obfuscate = (value: string) => {
    if (!value || value.length <= 8) return '••••••••';
    return '••••••••' + value.slice(-4);
  };

  const inputClasses =
    'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-primary-400 dark:focus:ring-primary-400';

  const textareaClasses =
    'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-primary-400 dark:focus:ring-primary-400 font-mono';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/50">
          <Fingerprint className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Identity Provider Configuration
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configure SSO with Microsoft Entra ID or Google Workspace.
          </p>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesRef} />
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {successMessage && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-green-50 dark:bg-green-900/20 p-4 text-sm text-green-700 dark:text-green-400">
          <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Cognito Info */}
      {ssoStatus && (
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg border bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                SP Entity ID (for your IdP)
              </p>
              <button
                onClick={() => toggleFieldVisibility('cognito-entity-id')}
                className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title={visibleFields.has('cognito-entity-id') ? 'Hide' : 'Show'}
              >
                {visibleFields.has('cognito-entity-id') ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
            <p className="text-sm font-mono text-gray-700 dark:text-gray-300 break-all">
              {visibleFields.has('cognito-entity-id')
                ? (ssoStatus.cognito_entity_id || 'Not available')
                : obfuscate(ssoStatus.cognito_entity_id)}
            </p>
          </div>
          <div className="rounded-lg border bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                ACS URL / Reply URL
              </p>
              <button
                onClick={() => toggleFieldVisibility('cognito-acs-url')}
                className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title={visibleFields.has('cognito-acs-url') ? 'Hide' : 'Show'}
              >
                {visibleFields.has('cognito-acs-url') ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
            <p className="text-sm font-mono text-gray-700 dark:text-gray-300 break-all">
              {visibleFields.has('cognito-acs-url')
                ? (ssoStatus.cognito_acs_url || 'Not available')
                : obfuscate(ssoStatus.cognito_acs_url)}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('microsoft-entra');
              clearMessages();
              setGroups([]);
            }}
            className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'microsoft-entra'
                ? 'border-primary-500 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Microsoft Entra ID
            {ssoStatus?.providers['microsoft-entra']?.configured && (
              <CheckCircle className="ml-1.5 inline h-4 w-4 text-green-500" />
            )}
          </button>
          <button
            onClick={() => {
              setActiveTab('google-workspace');
              clearMessages();
              setGroups([]);
            }}
            className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'google-workspace'
                ? 'border-primary-500 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Google Workspace
            {ssoStatus?.providers['google-workspace']?.configured && (
              <CheckCircle className="ml-1.5 inline h-4 w-4 text-green-500" />
            )}
          </button>
        </nav>
      </div>

      {/* Entra SSO Setup Guide */}
      {activeTab === 'microsoft-entra' && ssoStatus && (
        <EntraSsoSetupGuide
          cognitoEntityId={ssoStatus.cognito_entity_id}
          cognitoAcsUrl={ssoStatus.cognito_acs_url}
          signOnUrl={ssoStatus.providers['microsoft-entra']?.sign_on_url ?? ''}
          isProviderConfigured={ssoStatus.providers['microsoft-entra']?.configured ?? false}
        />
      )}

      {/* Provider Configuration Form */}
      <div className="space-y-6">
        {/* Status Badge */}
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              providerActive
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : providerConfigured
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300'
            }`}
          >
            {providerActive ? 'Active' : providerConfigured ? 'Configured' : 'Not configured'}
          </span>
        </div>

        {/* Microsoft Entra Form */}
        {activeTab === 'microsoft-entra' && (
          <form
            onSubmit={handleConfigureEntra}
            className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800 space-y-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Microsoft Entra ID Configuration
            </h3>

            <FormField label="Tenant ID" required htmlFor="entra-tenant-id">
              <div className="relative">
                <input
                  id="entra-tenant-id"
                  type={visibleFields.has('entra-tenant-id') ? 'text' : 'password'}
                  value={entraForm.tenantId}
                  onChange={(e) => setEntraForm({ ...entraForm, tenantId: e.target.value })}
                  className={inputClasses + ' pr-10'}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  required
                />
                <button
                  type="button"
                  onClick={() => toggleFieldVisibility('entra-tenant-id')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {visibleFields.has('entra-tenant-id') ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <FormField label="Client ID (App Registration)" required htmlFor="entra-client-id">
              <div className="relative">
                <input
                  id="entra-client-id"
                  type={visibleFields.has('entra-client-id') ? 'text' : 'password'}
                  value={entraForm.clientId}
                  onChange={(e) => setEntraForm({ ...entraForm, clientId: e.target.value })}
                  className={inputClasses + ' pr-10'}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  required
                />
                <button
                  type="button"
                  onClick={() => toggleFieldVisibility('entra-client-id')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {visibleFields.has('entra-client-id') ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <FormField label="Client Secret" htmlFor="entra-client-secret">
              <div className="relative">
                <input
                  id="entra-client-secret"
                  type="password"
                  value={entraForm.clientSecret}
                  onChange={(e) => {
                    setEntraForm({ ...entraForm, clientSecret: e.target.value });
                    if (e.target.value) setEntraHasStoredSecret(false);
                  }}
                  className={inputClasses}
                  placeholder={entraHasStoredSecret ? '••••••••••••••••' : 'Required for group fetching'}
                />
                {entraHasStoredSecret && !entraForm.clientSecret && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-600 dark:text-green-400">
                    Stored
                  </span>
                )}
              </div>
            </FormField>

            <FormField label="SAML Metadata URL" htmlFor="entra-metadata-url">
              <div className="relative">
                <input
                  id="entra-metadata-url"
                  type={visibleFields.has('entra-metadata-url') ? 'url' : 'password'}
                  value={entraForm.samlMetadataUrl}
                  onChange={(e) => setEntraForm({ ...entraForm, samlMetadataUrl: e.target.value })}
                  className={inputClasses + ' pr-10'}
                  placeholder="https://login.microsoftonline.com/{tenant}/federationmetadata/2007-06/federationmetadata.xml"
                />
                <button
                  type="button"
                  onClick={() => toggleFieldVisibility('entra-metadata-url')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {visibleFields.has('entra-metadata-url') ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={entraSubmitting}
                className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary-500 dark:hover:bg-primary-600 dark:focus:ring-offset-gray-800"
              >
                {entraSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {entraSubmitting ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </form>
        )}

        {/* Google Workspace Form */}
        {activeTab === 'google-workspace' && (
          <form
            onSubmit={handleConfigureGoogle}
            className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800 space-y-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Google Workspace Configuration
            </h3>

            <FormField label="Domain" required htmlFor="google-domain">
              <div className="relative">
                <input
                  id="google-domain"
                  type={visibleFields.has('google-domain') ? 'text' : 'password'}
                  value={googleForm.domain}
                  onChange={(e) => setGoogleForm({ ...googleForm, domain: e.target.value })}
                  className={inputClasses + ' pr-10'}
                  placeholder="yourdomain.com"
                  required
                />
                <button
                  type="button"
                  onClick={() => toggleFieldVisibility('google-domain')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {visibleFields.has('google-domain') ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <FormField label="SAML Metadata URL" htmlFor="google-metadata-url">
              <div className="relative">
                <input
                  id="google-metadata-url"
                  type={visibleFields.has('google-metadata-url') ? 'url' : 'password'}
                  value={googleForm.samlMetadataUrl}
                  onChange={(e) => setGoogleForm({ ...googleForm, samlMetadataUrl: e.target.value })}
                  className={inputClasses + ' pr-10'}
                  placeholder="https://accounts.google.com/..."
                />
                <button
                  type="button"
                  onClick={() => toggleFieldVisibility('google-metadata-url')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {visibleFields.has('google-metadata-url') ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <FormField label="Service Account JSON" htmlFor="google-service-account">
              <div className="relative">
                <textarea
                  id="google-service-account"
                  value={googleForm.serviceAccountJson}
                  onChange={(e) => {
                    setGoogleForm({ ...googleForm, serviceAccountJson: e.target.value });
                    if (e.target.value) setGoogleHasStoredServiceAccount(false);
                  }}
                  className={textareaClasses}
                  rows={4}
                  placeholder={googleHasStoredServiceAccount ? '••••••••••••••••' : '{"type": "service_account", ...}'}
                />
                {googleHasStoredServiceAccount && !googleForm.serviceAccountJson && (
                  <span className="absolute right-3 top-3 text-xs text-green-600 dark:text-green-400">
                    Stored
                  </span>
                )}
              </div>
            </FormField>

            <FormField label="Admin Email" htmlFor="google-admin-email">
              <div className="relative">
                <input
                  id="google-admin-email"
                  type={visibleFields.has('google-admin-email') ? 'email' : 'password'}
                  value={googleForm.adminEmail}
                  onChange={(e) => setGoogleForm({ ...googleForm, adminEmail: e.target.value })}
                  className={inputClasses + ' pr-10'}
                  placeholder="admin@yourdomain.com"
                />
                <button
                  type="button"
                  onClick={() => toggleFieldVisibility('google-admin-email')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {visibleFields.has('google-admin-email') ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={googleSubmitting}
                className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary-500 dark:hover:bg-primary-600 dark:focus:ring-offset-gray-800"
              >
                {googleSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {googleSubmitting ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </form>
        )}

        {/* Group Role Mapping */}
        {providerConfigured && (
          <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Group Role Mapping
              </h3>
              {!editingMappings ? (
                <button
                  onClick={() => setEditingMappings(true)}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  <Pencil className="h-4 w-4" />
                  Edit Mappings
                </button>
              ) : (
                <button
                  onClick={handleFetchGroups}
                  disabled={fetchingGroups}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  <RefreshCw className={`h-4 w-4 ${fetchingGroups ? 'animate-spin' : ''}`} />
                  {fetchingGroups ? 'Fetching...' : 'Refresh Groups from IdP'}
                </button>
              )}
            </div>

            {!editingMappings ? (
              /* Summary view — show mapped groups by role */
              (() => {
                const hasMappings = adminGroupIds.length > 0 || leadershipGroupIds.length > 0 || employeeGroupIds.length > 0;
                const getGroupName = (id: string) => groups.find(g => g.id === id)?.name || id;

                if (!hasMappings) {
                  return (
                    <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      No group mappings configured. Click "Edit Mappings" to assign IdP groups to roles.
                    </div>
                  );
                }

                const roles = [
                  { label: 'Admin', ids: adminGroupIds, color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
                  { label: 'Leadership', ids: leadershipGroupIds, color: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300' },
                  { label: 'Employee', ids: employeeGroupIds, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
                ];

                return (
                  <div className="space-y-3">
                    {roles.map((role) => (
                      <div key={role.label} className="flex items-start gap-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium flex-shrink-0 mt-0.5 w-20 justify-center ${role.color}`}>
                          {role.label}
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {role.ids.length > 0 ? (
                            role.ids.map((id) => (
                              <span
                                key={id}
                                className="inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300"
                              >
                                {getGroupName(id)}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-500 italic mt-0.5">
                              No groups assigned
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()
            ) : (
              /* Edit view — checkbox table */
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Select which IdP groups map to each role. Use "Refresh Groups from IdP" to load the latest groups.
                </p>

                {groups.length > 0 ? (
                  <>
                    <div className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
                      {/* Table header */}
                      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 bg-gray-50 px-4 py-2 dark:bg-gray-750">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                          Group
                        </span>
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 text-center w-20">
                          Admin
                        </span>
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 text-center w-20">
                          Leadership
                        </span>
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 text-center w-20">
                          Employee
                        </span>
                      </div>
                      {groups.map((group) => (
                        <div
                          key={group.id}
                          className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center px-4 py-3"
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {group.name}
                            </p>
                            {group.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {group.description}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center justify-center w-20">
                            <input
                              type="checkbox"
                              checked={adminGroupIds.includes(group.id)}
                              onChange={() => toggleGroupRole(group.id, 'admin')}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                            />
                          </div>
                          <div className="flex items-center justify-center w-20">
                            <input
                              type="checkbox"
                              checked={leadershipGroupIds.includes(group.id)}
                              onChange={() => toggleGroupRole(group.id, 'leadership')}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                            />
                          </div>
                          <div className="flex items-center justify-center w-20">
                            <input
                              type="checkbox"
                              checked={employeeGroupIds.includes(group.id)}
                              onChange={() => toggleGroupRole(group.id, 'employee')}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 flex justify-end gap-3">
                      <button
                        onClick={() => setEditingMappings(false)}
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          await handleSaveRoleMappings();
                          setEditingMappings(false);
                        }}
                        disabled={savingMappings}
                        className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary-500 dark:hover:bg-primary-600 dark:focus:ring-offset-gray-800"
                      >
                        {savingMappings && <Loader2 className="h-4 w-4 animate-spin" />}
                        {savingMappings ? 'Saving...' : 'Save Role Mappings'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    <p className="mb-3">No groups loaded. Fetch groups from your identity provider to configure role mappings.</p>
                    <button
                      onClick={handleFetchGroups}
                      disabled={fetchingGroups}
                      className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <RefreshCw className={`h-4 w-4 ${fetchingGroups ? 'animate-spin' : ''}`} />
                      {fetchingGroups ? 'Fetching...' : 'Fetch Groups from IdP'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Activate/Deactivate SSO */}
        {providerConfigured && (
          <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              SSO Activation
            </h3>
            <div className="flex items-center gap-4">
              {providerActive ? (
                <>
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <CheckCircle className="h-5 w-5" />
                    SSO is active with this provider.
                  </div>
                  <button
                    onClick={() => setShowDeactivateDialog(true)}
                    disabled={activating}
                    className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 shadow-sm transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-800 dark:bg-gray-700 dark:text-red-400 dark:hover:bg-gray-600"
                  >
                    <PowerOff className="h-4 w-4" />
                    Deactivate SSO
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Activate SSO to allow employees to sign in via this provider.
                  </p>
                  <button
                    onClick={handleActivateSso}
                    disabled={activating}
                    className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-green-500 dark:hover:bg-green-600 dark:focus:ring-offset-gray-800"
                  >
                    {activating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Power className="h-4 w-4" />
                    )}
                    {activating ? 'Activating...' : 'Activate SSO'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Deactivate Confirmation */}
      <ConfirmDialog
        isOpen={showDeactivateDialog}
        onConfirm={handleDeactivateSso}
        onCancel={() => setShowDeactivateDialog(false)}
        title="Deactivate SSO"
        message="Are you sure you want to deactivate SSO? Employees will no longer be able to sign in via their identity provider until SSO is re-activated."
        confirmLabel="Deactivate"
        variant="danger"
      />
    </div>
  );
}
